import React, { useEffect, useMemo, useState } from 'react';
import { Button, Label, Select, TextInput } from '../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { WizardIcon } from '../../components/ui/Icons';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { teachDocumentPosting } from '../../services/mutations';
import type { DocumentLine, DocumentPostingRole } from './useData';

type AccLookup = { id: number; code: string; name: string; isActive?: boolean; combLevel?: string | null };

export type TeachAmountStyle = 'TOTAL_VAT' | 'FULL_AMOUNT' | 'SCAN_LINES';

type Props = {
  open: boolean;
  onClose: () => void;
  documentId: number;
  payload: Record<string, unknown>;
  lines?: DocumentLine[];
  fileTypeLabel?: string;
  initialAmountStyle?: TeachAmountStyle;
  /** When true (submitted docs), only the business rule is saved — journal lines stay unchanged. */
  ruleOnly?: boolean;
  companies: AccLookup[];
  accounts: AccLookup[];
  docTypes: AccLookup[];
  onSaved: () => Promise<void> | void;
};

function numOrEmpty(value: unknown): number | '' {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return '';
}

function accountForRole(lines: DocumentLine[] | undefined, role: DocumentPostingRole): number | '' {
  const id = lines?.find((l) => l.role === role && l.accountId != null)?.accountId;
  return id != null ? Number(id) : '';
}

function companyFromLines(lines: DocumentLine[] | undefined): number | '' {
  const id = lines?.find((l) => l.companyId != null)?.companyId;
  return id != null ? Number(id) : '';
}

function formatMoney(value: number | ''): string {
  if (value === '') return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Prefill keywords from creditor invoice text on this document. */
function keywordsFromDocument(payload: Record<string, unknown>, lines: DocumentLine[]): string {
  const fromExpense = lines
    .filter((l) => l.role === 'EXPENSE' && typeof l.narrative === 'string' && l.narrative.trim())
    .map((l) => String(l.narrative).trim());

  const scanLines = Array.isArray(payload.lines) ? payload.lines : [];
  const fromScan = scanLines
    .map((row) =>
      row && typeof row === 'object' && typeof (row as { description?: unknown }).description === 'string'
        ? String((row as { description: string }).description).trim()
        : '',
    )
    .filter(Boolean);

  const fromPayload = [payload.description, payload.summary, payload.title, payload.narrative]
    .filter((v) => typeof v === 'string' && v.trim())
    .map((v) => String(v).trim());

  const parts = [...fromExpense, ...fromScan, ...fromPayload];
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const part of parts) {
    const key = part.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(part);
  }
  return unique.join(', ').slice(0, 250);
}

const STYLE_META: Record<TeachAmountStyle, { label: string; hint: string }> = {
  TOTAL_VAT: { label: 'Total + VAT', hint: 'Expense · VAT · Payable' },
  FULL_AMOUNT: { label: 'Full amount', hint: 'Expense · Payable' },
  SCAN_LINES: { label: 'Line by line', hint: 'One debit per scan line' },
};

export const TeachPostingWizard: React.FC<Props> = ({
  open,
  onClose,
  documentId,
  payload,
  lines = [],
  fileTypeLabel,
  initialAmountStyle,
  ruleOnly = false,
  companies,
  accounts,
  docTypes,
  onSaved,
}) => {
  const { colors } = useTheme();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [amountStyle, setAmountStyle] = useState<TeachAmountStyle>('TOTAL_VAT');
  const [defaultCompanyId, setDefaultCompanyId] = useState<number | ''>('');
  const [expenseAccountId, setExpenseAccountId] = useState<number | ''>('');
  const [vatAccountId, setVatAccountId] = useState<number | ''>('');
  const [payableAccountId, setPayableAccountId] = useState<number | ''>('');
  const [supplierKey, setSupplierKey] = useState('');
  const [matchKeywords, setMatchKeywords] = useState('');

  const netAmount = numOrEmpty(payload.netAmount);
  const vatAmount = numOrEmpty(payload.vatAmount ?? payload.vat ?? payload.taxAmount);
  const totalAmount = numOrEmpty(payload.totalAmount);
  const documentTypeId = Number.isInteger(Number(payload.documentTypeId)) ? Number(payload.documentTypeId) : '';
  const currencyCode = typeof payload.currencyCode === 'string' ? payload.currencyCode : '';

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAmountStyle(initialAmountStyle || 'TOTAL_VAT');
    setDefaultCompanyId(
      companyFromLines(lines) ||
        (Number.isInteger(Number(payload.defaultCompanyId)) ? Number(payload.defaultCompanyId) : ''),
    );
    setExpenseAccountId(accountForRole(lines, 'EXPENSE'));
    setVatAccountId(accountForRole(lines, 'VAT'));
    setPayableAccountId(accountForRole(lines, 'PAYABLE'));
    const creditor =
      typeof payload.supplierName === 'string' ? payload.supplierName.trim() : '';
    setSupplierKey(creditor);
    setMatchKeywords(keywordsFromDocument(payload, lines));
  }, [open, payload, initialAmountStyle, lines]);

  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: c.id, label: c.name })),
    [companies],
  );
  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: a.id, label: a.name || a.combLevel || a.code })),
    [accounts],
  );

  const journalTypeLabel = useMemo(() => {
    if (documentTypeId === '') return null;
    const d = docTypes.find((x) => x.id === documentTypeId);
    return d ? d.name : null;
  }, [documentTypeId, docTypes]);

  const needsVatAccount = amountStyle === 'TOTAL_VAT' && vatAmount !== '' && Number(vatAmount) > 0;
  const canSubmit =
    !!expenseAccountId &&
    !!payableAccountId &&
    (!needsVatAccount || !!vatAccountId) &&
    !!supplierKey.trim();

  const errMsg = (e: unknown) => {
    const msg = (e as any)?.response?.data?.message || (e as any)?.message || 'Request failed';
    return Array.isArray(msg) ? msg.join(', ') : String(msg);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const fullOnly = amountStyle === 'FULL_AMOUNT';
      const supplierName = supplierKey.trim();
      if (!supplierName) {
        setError('Supplier key is required — use the document Creditor so the rule is vendor-specific.');
        setBusy(false);
        return;
      }
      await teachDocumentPosting(documentId, {
        amountStyle,
        expenseAccountId: Number(expenseAccountId),
        vatAccountId: fullOnly ? null : vatAccountId === '' ? null : Number(vatAccountId),
        payableAccountId: Number(payableAccountId),
        documentTypeId: documentTypeId === '' ? null : Number(documentTypeId),
        defaultCompanyId: defaultCompanyId === '' ? null : Number(defaultCompanyId),
        saveAsRule: true,
        bindToSupplier: true,
        supplierName,
        matchKeywords: matchKeywords.trim() || null,
        netAmount: fullOnly ? null : netAmount === '' ? null : Number(netAmount),
        vatAmount: fullOnly ? 0 : vatAmount === '' ? null : Number(vatAmount),
        totalAmount: totalAmount === '' ? null : Number(totalAmount),
      });
      await onSaved();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const summaryChips = [
    fileTypeLabel,
    supplierKey.trim() || null,
    journalTypeLabel,
    currencyCode || null,
  ].filter(Boolean) as string[];

  return (
    <Modal isOpen={open} onClose={onClose} size="lg">
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
            <WizardIcon className="h-5 w-5" />
          </div>
          <div>
            <ModalTitle>Remember posting</ModalTitle>
            <p className={cn('text-xs', colors.textMuted)}>
              {ruleOnly
                ? 'Saves a business rule from this document’s accounts for future invoices — submitted lines stay unchanged.'
                : 'Uses the scanned invoice and journal lines on this page — only pick accounts if needed.'}
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalContent className="space-y-5">
        {error && (
          <div className={cn('rounded-xl border px-4 py-3 text-sm', colors.border, colors.error)}>
            {error}
          </div>
        )}

        <div className={cn('rounded-2xl border px-4 py-3', colors.border, colors.headerBg)}>
          <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>From this document</p>
          {summaryChips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {summaryChips.map((chip) => (
                <span
                  key={chip}
                  className={cn('rounded-lg border px-2 py-0.5 text-xs font-medium', colors.border, colors.text)}
                >
                  {chip}
                </span>
              ))}
            </div>
          )}
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div>
              <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Net</p>
              <p className={cn('mt-0.5 text-sm font-semibold tabular-nums', colors.text)}>{formatMoney(netAmount)}</p>
            </div>
            <div>
              <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>VAT</p>
              <p className={cn('mt-0.5 text-sm font-semibold tabular-nums', colors.text)}>{formatMoney(vatAmount)}</p>
            </div>
            <div>
              <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Payable</p>
              <p className={cn('mt-0.5 text-sm font-semibold tabular-nums', colors.text)}>{formatMoney(totalAmount)}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Supplier key</Label>
            <TextInput
              value={supplierKey}
              showClearButton={false}
              placeholder="From document Creditor"
              onChange={(e) => {
                setSupplierKey(e.target.value);
              }}
            />
            <p className={cn('mt-1 text-xs', colors.textMuted)}>
              Taken from the document Creditor field. The rule is bound to this supplier.
            </p>
          </div>
          <div className="sm:col-span-2">
            <Label>Keywords</Label>
            <TextInput
              value={matchKeywords}
              showClearButton={false}
              placeholder="e.g. software, microsoft, office"
              onChange={(e) => setMatchKeywords(e.target.value)}
            />
            <p className={cn('mt-1 text-xs', colors.textMuted)}>
              Prefills from expense narrative / scan lines on this document. Used to pick this expense account for
              matching products next time.
            </p>
          </div>
        </div>

        <div>
          <Label className="mb-2">Posting style</Label>
          <div
            className={cn(
              'grid grid-cols-3 gap-0.5 rounded-xl border p-1',
              colors.border,
              colors.headerBg,
            )}
            role="tablist"
            aria-label="Posting style"
          >
            {(Object.keys(STYLE_META) as TeachAmountStyle[]).map((style) => {
              const active = amountStyle === style;
              const meta = STYLE_META[style];
              return (
                <button
                  key={style}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  title={meta.hint}
                  onClick={() => setAmountStyle(style)}
                  className={cn(
                    'rounded-lg px-2.5 py-2 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40',
                    active
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.textSecondary, colors.interactiveHover),
                  )}
                >
                  <span className="block text-xs font-semibold leading-tight">{meta.label}</span>
                  <span
                    className={cn(
                      'mt-0.5 block truncate text-[10px] leading-tight',
                      active ? 'text-white/85' : colors.textMuted,
                    )}
                  >
                    {meta.hint}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Default company</Label>
            <Select
              value={defaultCompanyId}
              options={companyOptions}
              placeholder="Select company"
              showClearButton
              onChange={(e) => setDefaultCompanyId(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
          <div className={amountStyle === 'FULL_AMOUNT' || !needsVatAccount ? 'sm:col-span-2' : undefined}>
            <Label>Expense account</Label>
            <Select
              value={expenseAccountId}
              options={accountOptions}
              placeholder="Select expense account"
              showClearButton
              onChange={(e) => setExpenseAccountId(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
          {amountStyle !== 'FULL_AMOUNT' && (
            <div>
              <Label>VAT account{needsVatAccount ? '' : ' (optional)'}</Label>
              <Select
                value={vatAccountId}
                options={accountOptions}
                placeholder="Select VAT account"
                showClearButton
                onChange={(e) => setVatAccountId(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Payable account</Label>
            <Select
              value={payableAccountId}
              options={accountOptions}
              placeholder="Select supplier payable"
              showClearButton
              onChange={(e) => setPayableAccountId(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
        </div>

        <label className="flex items-start gap-3 rounded-xl border px-3 py-3 text-sm">
          <input type="checkbox" className="mt-1" checked readOnly disabled />
          <span>
            <span className={cn('font-medium', colors.text)}>
              {supplierKey.trim() ? `Bound to supplier: ${supplierKey.trim()}` : 'Supplier key required'}
            </span>
            <span className={cn('mt-0.5 block text-xs', colors.textMuted)}>
              Remember always creates a vendor-specific rule from the Creditor / Supplier key. Fill Creditor on the
              document if this field is empty.
            </span>
          </span>
        </label>
      </ModalContent>

      <ModalFooter>
        <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" loading={busy} disabled={busy || !canSubmit} onClick={submit}>
          Save rule & apply
        </Button>
      </ModalFooter>
    </Modal>
  );
};
